import { CheckCircle, Quote } from 'lucide-react';
import { ReviewStars } from './ReviewStars';
import type { Review } from '@/types/reviews';
import { cn } from '@/lib/utils';

interface ReviewCardProps {
  review: Review;
  compact?: boolean;
}

function relativeDate(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days < 7) return days <= 1 ? 'Hoje' : `Há ${days} dias`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `Há ${weeks} semana${weeks > 1 ? 's' : ''}`;
    const months = Math.floor(days / 30);
    if (months < 12) return `Há ${months} ${months === 1 ? 'mês' : 'meses'}`;
    return `Há mais de 1 ano`;
  } catch {
    return '';
  }
}

export const ReviewCard = ({ review, compact }: ReviewCardProps) => (
  <div className={cn(
    "bg-card border border-border rounded-xl relative hover:shadow-lg transition-shadow h-full flex flex-col",
    compact ? "p-5" : "p-7"
  )}>
    {/* Stylized opening quote — typography, not emoji */}
    <span
      aria-hidden
      className="absolute -top-2 right-5 font-serif text-primary/15 leading-none select-none"
      style={{ fontSize: compact ? "60px" : "80px" }}
    >
      &ldquo;
    </span>

    <div className="flex items-center gap-1 mb-4">
      <ReviewStars rating={review.rating} size="sm" />
    </div>

    <p className="text-foreground mb-5 leading-relaxed line-clamp-4 italic relative z-10">
      &ldquo;{review.body}&rdquo;
    </p>

    <div className="mt-auto pt-4 border-t border-border/40 flex flex-col gap-2">
      <div className="flex items-baseline gap-2 text-sm">
        <span className="font-semibold text-foreground">— {review.authorName}</span>
        {review.createdAt && (
          <span className="text-xs text-muted-foreground">· {relativeDate(review.createdAt)}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!compact && review.type === 'product' && review.productName && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/8 border border-primary/15 text-[11px] font-medium text-primary">
            {review.productName}
          </span>
        )}
        {review.verified && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-[11px] font-medium text-emerald-700">
            <CheckCircle className="w-3 h-3" />
            Verificado
          </span>
        )}
      </div>
    </div>
  </div>
);
